import { useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { capitalize, isEmpty, truncate } from "lodash";
import { useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { ToggleButton } from "@/Components/v2/atoms/Button";
import { Divider } from "@/Components/v2/atoms/Divider";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { BarChart } from "@/Components/v2/molecule/BarChart/BarChart";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import LegendItems from "@/Components/v2/molecule/LegendItems/LegendItems";
import NoData from "@/Components/v2/molecule/NoData";
import { WaterfallChart } from "@/Components/v2/molecule/WaterfallChart/WaterfallChart";
import Header from "@/Components/v2/organism/Header/Header";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useDefaultNodeName } from "@/hooks/shared/useDefaultNodeName";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ABBREVIATED_OPTION, NO_SYMBOL, useConfig } from "@/hooks/useConfig";
import { ONE_DECIMAL_OPTION } from "@/hooks/useConfig/numberAbbreviatorOptions";
import { useModuleName } from "@/hooks/useModuleName";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import Palette from "@/Utils/Colors/palette";
import { formatUnitValue } from "@/Utils/formatters";
import { Layer } from "@/Utils/hierarchy";

import { useFinancialReview } from "./api/useFinancialReview";
import { useRelianceRanking } from "./api/useRelianceRankingV2";
import ROIBreakdown from "./components/ROIBreakdown";
import { DEFAULT_COLUMNS } from "./constants/defaults";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { getTooltip } from "./helpers/getTooltip";
import { getBarLabel, tooltipOption } from "./helpers/salesCompositionChartHelpers";
import { useBusinessUnitChartOptions } from "./hooks/useBusinessUnitChartOptions";
import { useSalesCompositionChartOptions } from "./hooks/useSalesCompositionChartOptions";
import { useFinancialStore } from "./store/useFinancialStore";
import { useAvailableFilters } from "./useAvailableFilters";
import { useBusinessTermMapping } from "./utils/businessTermMapping";

const content = {
  title: "Financial Review",
  subtitle:
    "Uncover what drives your sales and pinpoint underperforming areas. Understand how pricing, discounting, and marketing impact sales and revenue across different business units.",
};

const noDataContent = {
  title: "No Data Available",
  description: "There is no financial data available for the selected filters.",
};

export const FinancialReviewV2 = () => {
  const { config } = useConfig();

  const { formatCurrency } = config.currency;
  const currencySymbol = config.currency.getSymbol();
  const apiRef = useRef(null);
  const theme = useTheme();
  const { loading: areGeosLoading, initInfo: info, nodes } = useInfo();
  const { moduleOpened, unitsSelected } = useAnalytics();
  const { moduleName } = useModuleName();
  const palette = new Palette(theme.palette.ROILineChart.barChartColors);

  const { mapBusinessTermName } = useBusinessTermMapping();
  const { value: salesUnitsEnabled } = useFeatureGate(featureFlag.salesUnitsEnabled);

  const cmsConfig = config?.graphs?.SalesByComposition;
  const salesUnitsLabel = cmsConfig?.salesUnitsLabel;
  const showSalesUnits = cmsConfig?.showSalesUnits;
  const { showReturnInsteadOfRevenue } = config;

  const {
    targetNodeUUID: selectedNodeUUID,
    filters,
    roiViewToggle,
    grid: { businessUnitToggle, geoView, activeColumns, metricType },
    setTargetNodeUUID,
    setBusinessUnitToggle,
    setGeoView,
    setActiveColumns,
    setMetricType,
    setROIViewToggle,
  } = useFinancialStore();

  useEffect(() => {
    if (showReturnInsteadOfRevenue) {
      setMetricType(MetricType.SALES_UNITS);
    }
  }, []);

  const { startDate, endDate } = useDateRangeStore();
  const selectedNode = nodes?.getNode(selectedNodeUUID ?? "");

  const { mapDefaultNodeName } = useDefaultNodeName(selectedNode);

  useEffect(() => {
    if (nodes && selectedNodeUUID === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNodeUUID(defaultNodeUUID);
      }
    }
  }, [nodes, setTargetNodeUUID, selectedNodeUUID]);

  const { data: financialReviewData, isLoading: isFinancialReviewLoading } = useFinancialReview({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    startDate: startDate,
    endDate: endDate,
    geographies: filters?.geographies ?? [],
    businessUnitToggle: businessUnitToggle,
    config: {
      enabled: !!selectedNode && !!startDate && !!endDate,
    },
  });

  const { data: relianceRankingData, isLoading: isRelianceRankingLoading } = useRelianceRanking({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    startDate: startDate,
    endDate: endDate,
    geographies: filters?.geographies ?? [],
    businessUnitToggle: businessUnitToggle,
    geoViewToggle: geoView,
    config: {
      enabled: !!selectedNode && !!startDate && !!endDate,
    },
  });

  const availableFilters = useAvailableFilters();

  const { salesCompositionData, total } = useSalesCompositionChartOptions({
    relianceRankingData,
    metricType,
  });

  const {
    axisLabels: businessUnitsAxisLabels,
    seriesData: businessUnitsSeries,
    legends: businessUnitsLegends,
    handleLegendItemClick,
    totals,
  } = useBusinessUnitChartOptions({
    relianceRankingData,
    palette,
  });

  const isLoading =
    nodes === null ||
    selectedNodeUUID === null ||
    isFinancialReviewLoading ||
    isRelianceRankingLoading ||
    areGeosLoading;

  // Use the column definitions
  const columns = getColumnDefinitions(activeColumns, {
    currencySymbol,
    formatCurrency,
    businessUnitToggle,
    geoView,
    metricType,
    mapDefaultNodeName,
    getLayerLabel: config.getLayerLabel,
    salesUnitsLabel,
  });

  const toggles: Toggle[] = [
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: BusinessUnitToggle.AGGREGATE },
        { label: "Split by Business Unit", value: BusinessUnitToggle.SPLIT },
      ],
    },
  ];

  const [geoData] = info?.geographies || [];

  if (geoData && geoData.length > 0) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoView,
      options: [
        { label: "Aggregate Geographies", value: GeoViewToggle.AGGREGATE },
        { label: "Split by Geography", value: GeoViewToggle.SPLIT },
      ],
    });
  }

  const unitsRevenueToggleOptions = [
    { label: capitalize(salesUnitsLabel) || "Units", value: MetricType.SALES_UNITS },
    { label: "Revenue", value: MetricType.SALES_REVENUE },
  ];

  const metricOptions = useMetricOptions("sales-only");

  const yAxisLabelFormatter = (value: number) => {
    if (metricType === MetricType.SALES_UNITS) {
      return config.numberAbbreviator.formatAbbreviatedNumber(value, ONE_DECIMAL_OPTION);
    }
    return formatCurrency(value, ABBREVIATED_OPTION);
  };

  const barLabelFormatter = (value: number) => {
    if (metricType === MetricType.SALES_UNITS) {
      return config.numberAbbreviator.formatAbbreviatedNumber(value, ONE_DECIMAL_OPTION);
    }
    return formatCurrency(value, ABBREVIATED_OPTION);
  };

  const tooltipFormatter = (value: number) => {
    if (metricType === MetricType.SALES_UNITS) {
      return `${formatUnitValue(value)} ${salesUnitsLabel || "units"}`;
    }
    return formatCurrency(value);
  };

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  const busUnitTooltipFormatOpt = showReturnInsteadOfRevenue ? NO_SYMBOL : {};
  const busUnitYAxisLabel = showReturnInsteadOfRevenue ? "Return" : "Revenue";
  const busUnitYAxisFormatOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...ABBREVIATED_OPTION }
    : ABBREVIATED_OPTION;

  useEffect(() => {
    moduleOpened("financial-review", Layer.Portfolio);
  }, []);

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : (
        <Box sx={{ marginX: 4 }}>
          {financialReviewData && (
            <ROIBreakdown
              data={financialReviewData}
              formatCurrency={formatCurrency}
              roiViewToggle={roiViewToggle}
              onToggleChange={setROIViewToggle}
            />
          )}
          <RoundedBorderContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack gap={0.5} maxWidth={600}>
                <Typography variant="h4" fontWeight={600} fontSize={24}>
                  Sales Composition
                </Typography>
                <Typography variant="body1" fontSize={14}>
                  Uncover sales drivers and optimize pricing and marketing strategies.
                </Typography>
              </Stack>
              {showSalesUnits && salesUnitsEnabled && !showReturnInsteadOfRevenue && (
                <ToggleButtonGroup exclusive value={metricType} sx={{ width: 200 }}>
                  {unitsRevenueToggleOptions.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      onClick={() => {
                        setMetricType(option.value as MetricType);
                        if (option.value === MetricType.SALES_UNITS) {
                          unitsSelected({ moduleName });
                        }
                      }}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </Stack>
            <Divider sx={{ my: 4 }} />
            <WaterfallChart
              data={salesCompositionData}
              total={total}
              yAxisLabelFormatter={yAxisLabelFormatter}
              xAxisLabelFormatter={(value: string) =>
                truncate(mapBusinessTermName(value), { length: 24 })
              }
              barLabel={getBarLabel(theme, salesCompositionData, total, barLabelFormatter)}
              tooltipOptions={tooltipOption(
                theme,
                salesCompositionData,
                total,
                tooltipFormatter,
                metricType
              )}
            />
          </RoundedBorderContainer>

          <RoundedBorderContainer>
            <Stack gap={0.5} maxWidth={600}>
              <Typography variant="h4" fontWeight={600} fontSize={24}>
                Business Units
              </Typography>
              <Typography variant="body1" fontSize={14}>
                Identify your top performers and understand which business units benefit from
                certain types of investment.
              </Typography>
            </Stack>
            <Divider sx={{ my: 4 }} />
            {!isEmpty(businessUnitsAxisLabels) && !isEmpty(businessUnitsSeries) ? (
              <>
                <BarChart
                  axisLabels={businessUnitsAxisLabels}
                  series={businessUnitsSeries}
                  tooltipOptions={getTooltip(
                    totals,
                    config.currency.formatCurrency,
                    busUnitTooltipFormatOpt,
                    mapDefaultNodeName
                  )}
                  yAxisLabel={busUnitYAxisLabel}
                  yAxisLabelFormatter={(value: number) =>
                    formatCurrency(value, busUnitYAxisFormatOptions)
                  }
                  xAxisLabelFormatter={(value: string) => mapDefaultNodeName(value)}
                  stacked
                />
                <LegendItems legends={businessUnitsLegends} handleClick={handleLegendItemClick} />
              </>
            ) : (
              <NoData title={noDataContent.title} description={noDataContent.description} />
            )}
          </RoundedBorderContainer>

          {/* Data Grid with Reliance Ranking Data */}
          <DataGridContainer
            apiRef={apiRef}
            rows={relianceRankingData?.tableView ?? []}
            columns={columns}
            metricDropdown={showReturnInsteadOfRevenue ? undefined : metricDropdownConfig}
            toolbarComponent={
              <ToolBarShared
                apiRef={apiRef}
                columns={columns}
                activeColumns={activeColumns}
                toggles={toggles}
                handleApplyFilters={({ activeColumns, toggles }) => {
                  setActiveColumns(activeColumns);
                  const businessUnitToggleValue = toggles["businessUnitToggle"];
                  if (businessUnitToggleValue) {
                    setBusinessUnitToggle(businessUnitToggleValue as BusinessUnitToggle);
                  }
                  const geoViewToggleValue = toggles["geoViewToggle"];
                  if (geoViewToggleValue) {
                    setGeoView(geoViewToggleValue as GeoViewToggle);
                  }
                }}
                handleResetFilters={() => {
                  setActiveColumns(DEFAULT_COLUMNS);
                  setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
                  setGeoView(GeoViewToggle.AGGREGATE);
                }}
              />
            }
            options={{
              rowHeight: 52,
              sortModel: [{ field: "brand_marketing_sale", sort: "desc" }],
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default FinancialReviewV2;
